import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowLeft, CheckCircle2, Lightbulb, Download, FileText } from "lucide-react";
import api, { track } from "@/lib/api";
import VideoPlayer from "@/components/VideoPlayer";
import ChessBoard from "@/components/ChessBoard";

function ExerciseBlock({ block, lessonId }) {
  const [status, setStatus] = useState(null); // correct | wrong | null
  const [wrongCount, setWrongCount] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [awarded, setAwarded] = useState(false);

  const onMove = async (mv) => {
    const correct = (block.correct_moves || []).map((m) => m.toLowerCase()).includes(mv.lan);
    if (correct) {
      setStatus("correct");
      if (!awarded) {
        setAwarded(true);
        try {
          const { data } = await api.post("/exercises/attempt", { correct: true, lesson_id: lessonId, block_id: block.id });
          if (data.xp_awarded > 0) toast.success(`Excelente! +${data.xp_awarded} XP`);
          else toast.success("Excelente! Lance correto.");
        } catch { toast.success("Excelente!"); }
      }
    } else {
      const wc = wrongCount + 1;
      setWrongCount(wc);
      setStatus("wrong");
      api.post("/exercises/attempt", { correct: false, lesson_id: lessonId, block_id: block.id }).catch(() => {});
      setTimeout(() => { setResetKey((k) => k + 1); setStatus(null); }, 900);
    }
  };

  return (
    <div className="rounded-xl border border-chessup-turq/30 bg-chessup-deep/40 p-5" data-testid="exercise-block">
      <div className="flex items-center gap-2 mb-1"><span className="text-xs font-mono uppercase tracking-widest text-chessup-turq">Agora é sua vez</span></div>
      <p className="text-chessup-text mb-4">{block.instruction}</p>
      <ChessBoard fen={block.fen} orientation={block.orientation || "white"} onMove={onMove} resetKey={resetKey} interactive={status !== "correct"} />
      <div className="mt-4 min-h-[40px]">
        {status === "correct" && (
          <div className="rounded-lg bg-chessup-turq/12 border border-chessup-turq/40 px-4 py-3 text-sm text-chessup-turq" data-testid="exercise-correct">
            <CheckCircle2 className="inline h-4 w-4 mr-1.5" /> Excelente! {block.explanation}
          </div>
        )}
        {status === "wrong" && (
          <div className="rounded-lg bg-destructive/15 border border-destructive/40 px-4 py-3 text-sm text-red-200" data-testid="exercise-wrong">Quase. Tente outra vez.</div>
        )}
        {status !== "correct" && wrongCount >= 2 && (
          <div className="mt-2">
            <button onClick={() => setShowHint(true)} data-testid="exercise-hint-btn" className="inline-flex items-center gap-1.5 text-sm text-chessup-goldlight hover:underline"><Lightbulb className="h-4 w-4" /> Quer uma dica?</button>
            {showHint && <p className="mt-2 text-sm text-chessup-subtext italic">{block.hint}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function QuizBlock({ block }) {
  const [picked, setPicked] = useState(null);
  return (
    <div className="rounded-xl border border-chessup-border bg-chessup-card p-5" data-testid="quiz-block">
      <p className="text-chessup-text font-medium mb-4">{block.question}</p>
      <div className="space-y-2">
        {block.options.map((opt, i) => {
          const isAns = i === block.answer;
          const show = picked !== null;
          return (
            <button key={opt} onClick={() => setPicked(i)} disabled={picked !== null}
              className={`w-full text-left rounded-lg border px-4 py-2.5 text-sm transition-all ${show && isAns ? "border-chessup-turq bg-chessup-turq/12 text-chessup-turq" : show && picked === i ? "border-destructive bg-destructive/10 text-red-200" : "border-chessup-border text-chessup-text hover:border-chessup-turq/50"}`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Block({ block, lessonId }) {
  switch (block.type) {
    case "TITLE": return <h2 className="font-heading text-2xl text-chessup-text">{block.text}</h2>;
    case "TEXT": return <p className="text-chessup-subtext leading-relaxed">{block.text}</p>;
    case "IMAGE": return <figure><img src={block.url} alt={block.caption || ""} className="rounded-xl w-full" /><figcaption className="text-xs text-chessup-subtext mt-2">{block.caption}</figcaption></figure>;
    case "CHESS_POSITION": return <div><ChessBoard fen={block.fen} orientation={block.orientation || "white"} interactive={false} />{block.caption && <p className="text-center text-xs text-chessup-subtext mt-2">{block.caption}</p>}</div>;
    case "INTERACTIVE_EXERCISE": return <ExerciseBlock block={block} lessonId={lessonId} />;
    case "QUIZ": return <QuizBlock block={block} />;
    case "TIP": return <div className="rounded-xl border border-chessup-gold/30 bg-chessup-gold/8 p-4 flex gap-3"><Lightbulb className="h-5 w-5 text-chessup-goldlight shrink-0 mt-0.5" /><p className="text-sm text-chessup-text">{block.text}</p></div>;
    case "SUMMARY": return <div className="rounded-xl border border-chessup-turq/30 bg-chessup-turq/8 p-4 flex gap-3"><FileText className="h-5 w-5 text-chessup-turq shrink-0 mt-0.5" /><p className="text-sm text-chessup-text">{block.text}</p></div>;
    case "DOWNLOAD": return <a href={block.url} className="inline-flex items-center gap-2 rounded-lg border border-chessup-border px-4 py-2.5 text-sm text-chessup-text hover:border-chessup-turq"><Download className="h-4 w-4 text-chessup-turq" /> {block.label}</a>;
    case "VIDEO": return null;
    default: return null;
  }
}

export default function Lesson() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [completed, setCompleted] = useState(false);
  const startPos = useRef(0);

  useEffect(() => {
    api.get(`/lessons/${id}`).then(({ data }) => {
      setLesson(data);
      setCompleted(data.progress?.completed || false);
      startPos.current = data.progress?.last_position || 0;
    }).catch(() => {});
  }, [id]);

  if (!lesson) return <div className="p-8"><div className="h-8 w-56 bg-chessup-card rounded animate-pulse" /></div>;

  if (lesson.locked) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <div className="rounded-2xl border border-chessup-gold/40 bg-chessup-gold/8 p-10">
          <Lock className="h-10 w-10 text-chessup-goldlight mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-chessup-text">🔒 Conteúdo exclusivo para membros ChessUp Academy</h1>
          <p className="text-chessup-subtext mt-3">Gostou da aula? Isso é só o começo.</p>
          <Link to="/assinatura" className="mt-6 inline-block rounded-full bg-chessup-gold px-7 py-3 font-semibold text-chessup-deep hover:bg-chessup-goldlight">DESBLOQUEAR CHESSUP ACADEMY</Link>
        </div>
      </div>
    );
  }

  const videoBlock = (lesson.blocks || []).find((b) => b.type === "VIDEO");
  const videoUrl = videoBlock?.url || lesson.video_url;
  const otherBlocks = (lesson.blocks || []).filter((b) => b.type !== "VIDEO");

  const saveProgress = (secs) => api.post(`/lessons/${id}/progress`, { last_position: secs }).catch(() => {});

  const complete = async () => {
    try {
      const { data } = await api.post(`/lessons/${id}/complete`);
      setCompleted(true);
      if (data.xp_awarded > 0) toast.success(`Aula concluída! +${data.xp_awarded} XP`);
      else toast.success("Aula concluída!");
      track("lesson_completed", "course", { id });
    } catch { toast.error("Não foi possível concluir agora."); }
  };

  return (
    <div className="max-w-3xl mx-auto p-5 sm:p-8">
      <Link to={lesson.course_id ? `/cursos/${lesson.course_id}` : "/cursos"} className="inline-flex items-center gap-1 text-sm text-chessup-subtext hover:text-chessup-turq mb-5"><ArrowLeft className="h-4 w-4" /> Voltar ao curso</Link>
      <h1 className="font-heading text-3xl text-chessup-text mb-1">{lesson.title}</h1>
      <p className="text-sm text-chessup-subtext mb-6">{lesson.description}</p>

      {videoUrl && (
        <div className="mb-8">
          <VideoPlayer src={videoUrl} poster={lesson.thumbnail} startAt={startPos.current}
            onStart={() => track("video_started", "course", { id })}
            onProgress={saveProgress}
            onEnded={() => { saveProgress(0); track("video_completed", "course", { id }); }} />
        </div>
      )}

      <div className="space-y-6">
        {otherBlocks.map((b) => <Block key={b.id} block={b} lessonId={id} />)}
      </div>

      <div className="mt-10 flex items-center justify-between border-t border-chessup-border pt-6">
        {completed ? (
          <span className="inline-flex items-center gap-2 text-chessup-turq"><CheckCircle2 className="h-5 w-5" /> Aula concluída</span>
        ) : (
          <button onClick={complete} data-testid="complete-lesson-btn" className="rounded-full bg-chessup-gold px-6 py-3 font-semibold text-chessup-deep hover:bg-chessup-goldlight">MARCAR COMO CONCLUÍDA</button>
        )}
        <Link to="/cursos" className="text-sm text-chessup-subtext hover:text-chessup-turq">Próxima aula →</Link>
      </div>
    </div>
  );
}
